import { useCallback } from "react";
import { CRMPlatform } from "@/enums/CRM.enum";
import { capitalizeFirstLetter } from "@/utils/helper.util";
import { useTranslation } from "react-i18next";
import { HiOutlineExclamationTriangle } from "react-icons/hi2";

import { useCRMDisconnect } from "@/hooks/api/crm/useCRMDisconnect";
import Button from "@/components/atoms/Button";
import FeaturedIcon from "@/components/atoms/FeaturedIcon";
import BasicModalContent from "@/components/atoms/modal-panels/BasicModalContent";

type Props = {
  onClose: (value: boolean) => void;
  preventClose?: boolean;
  onSuccess: () => void;
  platform: CRMPlatform;
};

/**
 * Modal to remove the CRM connection
 */
const CRMRemoveConnectionModal = ({ onClose, preventClose = false, onSuccess, platform }: Props) => {
  const { t } = useTranslation();

  const { disconnectCRMAsync, isPending } = useCRMDisconnect();

  const removeConnection = useCallback(() => {
    disconnectCRMAsync().then(() => onSuccess());
  }, [disconnectCRMAsync, onSuccess]);

  return (
    <BasicModalContent
      featuredIcon={<FeaturedIcon icon={HiOutlineExclamationTriangle} size={"lg"} color={"warning"} />}
      heading={t("crm.connections.remove.modal.heading", { platform: capitalizeFirstLetter(platform) })}
      preventClose={preventClose}
      body={t("crm.connections.remove.modal.description")}
      onClose={onClose}
      data-testid={"remove-crm-modal"}
    >
      <div className="mt-4 grid grid-cols-2 gap-3">
        <Button type={"button"} label={t("cancel")} onClick={() => onClose(false)} color="natural" variants="outline" />
        <Button
          type="button"
          label={t("crm.connections.remove.modal.button")}
          isLoading={isPending}
          onClick={() => removeConnection()}
          color="error"
          variants="primary"
        />
      </div>
    </BasicModalContent>
  );
};

export default CRMRemoveConnectionModal;
